<template>
  <Box>
    <BoxHead icon="fas fa-exchange-alt">
      Income & Expenses
    </BoxHead>

    <BoxLoader v-if="loading" />

    <EmptySection
      v-else-if="isEmpty"
      :badges="['Income', 'Expenses']"
      :entity-external-id="entityExternalId"
    />

    <template v-else>
      <Stack gap="2">
        <Recommendations :recommendations="recommendations" />

        <DetailsTitle>
          Income Sources
        </DetailsTitle>

        <DataTable
          :head="incomeSourcesTable.head"
          :body="incomeSourcesTable.body"
          :foot="incomeSourcesTable.foot"
        />
      </Stack>

      <Stack gap="2">
        <DetailsTitle>
          Expenses
        </DetailsTitle>

        <DataTable
          :head="expensesTable.head"
          :body="expensesTable.body"
          :foot="expensesTable.foot"
        />
      </Stack>

      <DetailsTitle
        icon="fas fa-money-bill"
        :value="formatCurrency(monthlyCashFlow)"
      >
        <FormulaTooltip
          v-if="monthlyCashFlowFormula"
          :formula="monthlyCashFlowFormula"
          :entity-external-id="entityExternalId"
        >
          Monthly Cash Flow
        </FormulaTooltip>
        <span v-else>
          Monthly Cash Flow
        </span>
      </DetailsTitle>
    </template>
  </Box>
</template>

<script setup>
import { computed } from 'vue';
import { Box, BoxHead, Stack, DataTable, DetailsTitle, BoxLoader } from '@stakeui';
import Recommendations from './Recommendations.vue';
import { formatCurrency } from '@frontend/shared/Util.js';
import { isEmptyValue } from '@frontend/shared/Util.js';
import EmptySection from './EmptySection.vue';
import FormulaTooltip from '@frontend/ui/components/FormulaTooltip.vue';

const props = defineProps({
  client: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  recommendations: {
    type: Array,
    default: () => ([]),
  },
  entityExternalId: {
    type: String,
    required: true,
  },
});

const incomeSources = computed(() => props.client?.income_sources);
const totalAnnualIncome = computed(() => props.client?.total_annual_income);
const expenses = computed(() => props.client?.expenses);
const totalMonthlyExpenses = computed(() => props.client?.total_monthly_expenses);
const monthlyCashFlow = computed(() => props.client?.monthly_cash_flow);

const formulas = computed(() => props.client?._formulas || {});
const monthlyCashFlowFormula = computed(() => formulas.value.monthly_cash_flow);

const isEmpty = computed(() => {
  return isEmptyValue(incomeSources.value) &&
         isEmptyValue(totalAnnualIncome.value) &&
         isEmptyValue(expenses.value) &&
         isEmptyValue(totalMonthlyExpenses.value) &&
         isEmptyValue(monthlyCashFlow.value);
});

const incomeSourcesTable = computed(() => {
  if (!incomeSources?.value) {
    return {};
  }

  const body = incomeSources.value.map((income, index) => ({
    id: index,
    cells: [
      { id: 'source', label: income.source },
      { id: 'frequency', label: income.frequency },
      { id: 'amount', label: formatCurrency(income.amount), type: 'numeric' },
    ],
  }
  ));

  body.push({
    id: 'total',
    variant: 'bold',
    cells: [
      { id: 'source', label: 'Total Annual Income', colSpan: 2 },
      { id: 'amount', label: formatCurrency(totalAnnualIncome.value), type: 'numeric' },
    ],
  });

  return {
    head: [
      { id: 'source', label: 'Source' },
      { id: 'frequency', label: 'Frequency', width: '8em' },
      { id: 'amount', label: 'Amount', type: 'numeric', width: '8em' },
    ],
    body,
  };
});

const expensesTable = computed(() => {
  if (!expenses?.value) {
    return {};
  }

  const body = expenses.value.map((expense, index) => ({
    id: index,
    cells: [
      { id: 'type', label: expense.type },
      { id: 'frequency', label: expense.frequency },
      { id: 'amount', label: formatCurrency(expense.amount), type: 'numeric' },
    ],
  }
  ));

  body.push({
    id: 'total',
    variant: 'bold',
    cells: [
      { id: 'type', label: 'Total Monthly Expenses', colSpan: 2 },
      { id: 'amount', label: formatCurrency(totalMonthlyExpenses.value), type: 'numeric' },
    ],
  });

  return {
    head: [
      { id: 'type', label: 'Type' },
      { id: 'frequency', label: 'Frequency', width: '8em' },
      { id: 'amount', label: 'Amount', type: 'numeric', width: '8em' },
    ],
    body,
  };
});
</script>
